/**
 * Phase 2 slice 2 — entity-owned investment property sale proceeds → entity liquid pool.
 * Spec §5.6, §5.9.2, §5.9.2a.
 */
import { getInvestmentPropertyTaxSummary } from '../../../shared/src/calculation/entityDisposalTax.ts';
import { applyDownsizerContributionsToAssets } from '../../../shared/src/calculation/downsizerContributionUtils.ts';
import {
    entityOwnershipValue,
    getEntityIdFromOwnership,
    isEntityOwnership,
} from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { assetIdsMatch } from './entityFundingUtils.ts';
import type { FactFind, FactFindAsset, FactFindAssetMap } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

type DraftAsset = FactFindAsset;
type DraftAssetMap = NonNullable<DraftFactFind['assets']>;

/** Investment-property sale allocation inputs (manual control / suggestion-engine scenario). */
export interface InvestmentPropertySaleDetails {
    selectedPropertyId?: string | number;
    salePrice?: number;
    sellingCosts?: number;
    costBase?: number;
    trustDisposal?: string;
    radAllocation?: number;
    newHomePurchasePrice?: number;
    downsizerContribution?: number;
    partnerDownsizerContribution?: number;
    commenceIncomeStream?: boolean;
    giftingAmount?: number;
    funeralBondPurchase?: number;
    cgtOverrideTotal?: number;
    [key: string]: unknown;
}

export const ENTITY_IP_PROCEEDS_ID_PREFIX = 'entity-ip-proceeds-';

export function getSelectedInvestmentProperty(
    factFind: DraftFactFind | null | undefined,
    details: InvestmentPropertySaleDetails | null | undefined,
): DraftAsset | null {
    if (!factFind?.assets?.investmentProperty?.length || !details) return null;
    const selectedPropertyId = details.selectedPropertyId;
    return (
        factFind.assets.investmentProperty.find((p) => assetIdsMatch(p.id, selectedPropertyId)) ||
        factFind.assets.investmentProperty[0]!
    );
}

/**
 * Trust/company (and other non-partnership entities): cash stays in entity pool.
 * Partnership: gain taxed personally — unallocated proceeds use personal bank (spec §5.9.2).
 */
export function routesIpProceedsToEntityPool(
    factFind: DraftFactFind,
    asset: DraftAsset | null | undefined,
): boolean {
    if (!asset || !isEntityOwnership(asset.ownership)) return false;
    const entityId = getEntityIdFromOwnership(asset.ownership);
    const structure = (factFind.structures || []).find((s) => s.id === entityId);
    return structure?.type !== 'partnership';
}

export function sumInvestmentPropertyAllocations(details: InvestmentPropertySaleDetails = {}): number {
    return (
        (details.radAllocation || 0) +
        (details.newHomePurchasePrice || 0) +
        (details.downsizerContribution || 0) +
        (details.partnerDownsizerContribution || 0) +
        (details.giftingAmount || 0) +
        (details.funeralBondPurchase || 0)
    );
}

function defaultBankIncomeRate(assets: DraftAssetMap | undefined): number {
    const list = assets?.bankAccounts;
    if (list && list.length > 0) return list[0]!.incomeRate || 2;
    return 2;
}

interface CreditLiquidProceedsOptions {
    ownership: string;
    id: string;
    description: string;
    mergeEntityId?: string;
    incomeRate?: number;
}

/**
 * Credit cash to bankAccounts — merge into existing entity bank when mergeEntityId set.
 */
export function creditLiquidProceeds(
    assets: DraftAssetMap,
    amount: number,
    options: CreditLiquidProceedsOptions,
): { credited: number; bankId: string | number | null } {
    const proceeds = Math.max(0, amount || 0);
    if (proceeds <= 0) return { credited: 0, bankId: null };

    const { ownership, id, description, mergeEntityId, incomeRate } = options;
    if (!assets.bankAccounts) assets.bankAccounts = [];

    if (mergeEntityId) {
        const entityOwn = entityOwnershipValue(mergeEntityId);
        const existing = assets.bankAccounts.find(
            (a) =>
                a.ownership === entityOwn &&
                !String(a.id || '').startsWith(ENTITY_IP_PROCEEDS_ID_PREFIX)
        );
        if (existing) {
            existing.value = (existing.value || 0) + proceeds;
            return { credited: proceeds, bankId: existing.id ?? null };
        }
    }

    const existingById = assets.bankAccounts.find((a) => assetIdsMatch(a.id, id));
    if (existingById) {
        existingById.value = (existingById.value || 0) + proceeds;
        return { credited: proceeds, bankId: existingById.id ?? null };
    }

    assets.bankAccounts.push({
        id,
        type: 'bankAccounts',
        description,
        value: proceeds,
        ownership,
        incomeRate: incomeRate ?? defaultBankIncomeRate(assets),
        growthRate: 0,
    });
    return { credited: proceeds, bankId: id };
}

/**
 * Credit unallocated net proceeds after allocations (preview or post-sale).
 */
export function creditInvestmentPropertyUnallocatedProceeds(
    data: DraftFactFind,
    factFind: DraftFactFind,
    details: InvestmentPropertySaleDetails,
): { unallocated: number; routedToEntity: boolean; entityId: string | null } {
    const property = getSelectedInvestmentProperty(factFind, details);
    // DraftFactFind is structurally compatible with FactFind (see types/formDraft.ts);
    // the shared calc-engine fn wants the strict type but defensively parseFloat/coerces internally
    // (see factFindTypes.ts header) regardless, so this cast is safe.
    const summary = getInvestmentPropertyTaxSummary(factFind as unknown as FactFind, details);
    const netProceeds =
        summary?.netProceeds ??
        (details.salePrice || 0) - (details.sellingCosts || 0);
    const unallocated = netProceeds - sumInvestmentPropertyAllocations(details);
    if (unallocated <= 0) {
        return { unallocated: 0, routedToEntity: false, entityId: null };
    }

    const incomeRate = defaultBankIncomeRate(data.assets);
    const toEntity = !!(property && routesIpProceedsToEntityPool(factFind, property));

    if (toEntity) {
        const entityId = getEntityIdFromOwnership(property!.ownership) ?? '';
        const structure = (factFind.structures || []).find((s) => s.id === entityId);
        const name = structure?.name?.trim() || 'Trust or company';
        if (!data.assets) data.assets = {};
        creditLiquidProceeds(data.assets, unallocated, {
            ownership: entityOwnershipValue(entityId),
            id: `${ENTITY_IP_PROCEEDS_ID_PREFIX}${entityId}`,
            description: `Unallocated proceeds — ${name} (from property sale)`,
            mergeEntityId: entityId,
            incomeRate,
        });
        return { unallocated, routedToEntity: true, entityId };
    }

    if (!data.assets) data.assets = {};
    creditLiquidProceeds(data.assets, unallocated, {
        ownership: 'joint',
        id: 'investment-proceeds',
        description: 'Unallocated Investment Sale Proceeds',
        incomeRate,
    });
    return { unallocated, routedToEntity: false, entityId: null };
}

export function removeSoldInvestmentProperty(assets: DraftAssetMap | undefined, propertyId: string | number | undefined): void {
    if (!assets?.investmentProperty) return;
    assets.investmentProperty = assets.investmentProperty.filter(
        (p) => !assetIdsMatch(p.id, propertyId)
    );
}

/**
 * Apply whole investment property sale to a cloned factFind (Aged Care manual control).
 * @param data — mutable clone
 * @param factFind — baseline for tax / structure lookup
 * @param details — investmentPropertyDetails
 */
export function applyInvestmentPropertySaleToFactFind(
    data: DraftFactFind,
    factFind: DraftFactFind,
    details: InvestmentPropertySaleDetails,
): DraftFactFind {
    const property = getSelectedInvestmentProperty(factFind, details);
    if (!property) return data;

    removeSoldInvestmentProperty(data.assets, property.id);

    if (!data.home) data.home = {};
    if ((details.newHomePurchasePrice || 0) > 0) {
        data.home.value = details.newHomePurchasePrice;
    }

    if (!data.assets) data.assets = {};

    if ((details.radAllocation || 0) > 0) {
        // 'radPaid' is a synthetic bookkeeping row, not one of the catalog AssetType ids.
        data.assets.radPaid = [
            {
                id: 'rad-paid',
                type: 'radPaid',
                description: 'RAD Paid',
                value: details.radAllocation,
                ownership: 'client',
            } as unknown as DraftAsset,
        ];
    }

    creditInvestmentPropertyUnallocatedProceeds(data, factFind, details);

    // DraftFactFind is structurally compatible with FactFind/FactFindAssetMap (see
    // types/formDraft.ts); the shared calc-engine fn defensively coerces internally regardless, so
    // this cast is safe (see factFindTypes.ts header comment).
    applyDownsizerContributionsToAssets(
        data.assets as unknown as FactFindAssetMap,
        factFind as unknown as FactFind,
        details,
        {
            idPrefix: 'investment-downsizer-contrib',
            assetId: 'investment-downsizer-contrib',
            partnerAssetId: 'partner-investment-downsizer-contrib',
        },
    );

    if ((details.funeralBondPurchase || 0) > 0) {
        if (!data.assets.funeralBonds) data.assets.funeralBonds = [];
        data.assets.funeralBonds.push({
            id: 'investment-fbond',
            type: 'funeralBonds',
            description: 'Funeral Bond from Proceeds',
            value: details.funeralBondPurchase,
            ownership: 'client',
        });
    }

    return data;
}
